import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Avatar, Button, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BaseLayout from './BaseLayout';
import { env } from '../config/env';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { authApi } from '../services/authApi';
import ThemeToggle from '../components/ThemeToggle';
import { useColorScheme } from '@mui/material';

// Navigation links - public pages visible to all users
const publicNavItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
];

// Navigation links - private pages visible only to logged-in users
const privateNavItems = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Positions", path: "/positions" },
  { label: "Orders", path: "/orders" },
  { label: "Watchlist", path: "/watchlist" },
  { label: "Markets", path: "/markets" },
  { label: "Captin AI", path: "/captin-ai" },
  { label: "Discover", path: "/discover" },
  { label: "Settings", path: "/settings" },
];

const PublicLayout = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { mode, setMode } = useColorScheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Navigation menu state
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleLogout = async () => {
    try {
      // Call the logout API endpoint
      await authApi.endpoints.logout.initiate().unwrap();
      // The logout action is already dispatched in the authApi onQueryStarted
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if API call fails, clear local state and redirect
      dispatch(logout());
      navigate("/");
    }
    handleCloseUserMenu();
  };

  return (
    <BaseLayout>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'inherit',
          color: mode === 'light' ? 'text.primary' : 'white',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo for desktop */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mx: 2,
                display: { xs: 'none', md: 'flex' },
                color: mode === 'light' ? 'text.primary' : 'white'
              }}
            >
              <Box component="img" src={`assets/fulllogo${mode === 'light' ? '' : '-dark'}.webp`} alt="Logo" width="auto" height={60} />
            </Box>

            {/* Mobile menu icon and dropdown */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color={mode === 'light' ? 'text.primary' : 'white'}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {/* Show public pages to everyone */}
                {!isAuthenticated && publicNavItems.map((item) => (
                  <MenuItem 
                    key={item.path}
                    onClick={handleCloseNavMenu}
                    component={RouterLink}
                    to={item.path}
                  >
                    <Typography textAlign="center">{item.label}</Typography>
                  </MenuItem>
                ))}
                
                {/* Show private pages only to authenticated users */}
                {isAuthenticated && privateNavItems.map((item) => (
                  <MenuItem 
                    key={item.path}
                    onClick={handleCloseNavMenu}
                    component={RouterLink}
                    to={item.path}
                  >
                    <Typography textAlign="center">{item.label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            
            {/* Logo for mobile */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                color: mode === 'light' ? 'text.primary' : 'white',
              }}
            >
              <Box component="img" src={`assets/fulllogo${mode === 'light' ? '' : '-dark'}.webp`} alt="Logo" width="auto" height={60} />
            </Box>
            
            {/* Desktop navigation links */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {/* Show public pages to everyone */}
              {!isAuthenticated && publicNavItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  onClick={handleCloseNavMenu}
                  sx={{ my: 2, color: mode === 'light' ? 'text.primary' : 'white', display: 'block' }}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Show private pages only to authenticated users */}
              {isAuthenticated && privateNavItems.map((item) => (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  onClick={handleCloseNavMenu}
                  sx={{ my: 2, color: mode === 'light' ? 'text.primary' : 'white', display: 'block' }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* User menu section */}
            <Box sx={{ flexGrow: 0 }}>
              {isAuthenticated ? (
                <>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar-user"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem onClick={handleLogout}>
                      <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/login"
                >
                  Login
                </Button>
              )}
            </Box>

            <ThemeToggle />
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflowX: "hidden",
        }}
      >
        <Container maxWidth="xl" sx={{ width: "100%", py: 2 }}>
          {children || <Outlet />}
        </Container>
      </Box>
      
      {/* Footer */}
      <Box component="footer" sx={{ py: 3, bgcolor: 'background.paper', textAlign: 'center', width: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} {env.appTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Environment: {env.appEnv}
        </Typography>
      </Box>
    </BaseLayout>
  );
};

PublicLayout.propTypes = {
  children: PropTypes.node,
};

export default PublicLayout;
